import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';
import Header from '../components/Header';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <Header />

      <div className="pt-10 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <span className="mx-2">›</span>
            <span className="text-white">Politique de confidentialité</span>
          </nav>

          {/* Header */}
          <div className="flex items-center space-x-4 mb-8">
            <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
              <Shield className="w-7 h-7 text-[#191C1F]" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900">Politique de confidentialité</h1>
              <p className="text-gray-400">Dernière mise à jour : 10 février 2026</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-8">
              <p className="text-gray-600 m-0">
                Chez alphaswapp, nous accordons une importance capitale à la protection de vos données personnelles. 
                Cette politique explique comment nous collectons, utilisons et protégeons vos informations.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Données collectées</h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <p className="text-gray-600 mb-4">Nous collectons les informations suivantes :</p>
                <ul className="space-y-2 text-gray-500">
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-[#191C1F] rounded-full mt-2"></span>
                    <span><strong>Données d'identification :</strong> nom, prénom, adresse email, numéro de téléphone</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-[#191C1F] rounded-full mt-2"></span>
                    <span><strong>Données financières :</strong> adresses de portefeuilles crypto, coordonnées bancaires</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-[#191C1F] rounded-full mt-2"></span>
                    <span><strong>Données de transaction :</strong> historique des échanges, montants, dates</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-[#191C1F] rounded-full mt-2"></span>
                    <span><strong>Données techniques :</strong> adresse IP, type de navigateur, cookies</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Utilisation des données</h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <p className="text-gray-600 mb-4">Vos données sont utilisées pour :</p>
                <ul className="space-y-2 text-gray-500">
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2"></span>
                    <span>Traiter et exécuter vos transactions d'échange</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2"></span>
                    <span>Vérifier votre identité conformément aux réglementations AML/KYC</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2"></span>
                    <span>Vous envoyer des notifications sur vos transactions</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2"></span>
                    <span>Améliorer nos services et votre expérience utilisateur</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2"></span>
                    <span>Prévenir la fraude et les activités illégales</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. Sécurité des données</h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <p className="text-gray-600">
                  Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger 
                  vos données contre tout accès non autorisé, modification, divulgation ou destruction. Cela inclut le 
                  chiffrement SSL/TLS, le stockage sécurisé des données et des contrôles d'accès stricts.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Vos droits</h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <p className="text-gray-600 mb-4">Conformément au RGPD, vous disposez des droits suivants :</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-100">
                    <h4 className="font-semibold text-gray-900 mb-1">Droit d'accès</h4>
                    <p className="text-sm text-gray-500">Obtenir une copie de vos données</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-100">
                    <h4 className="font-semibold text-gray-900 mb-1">Droit de rectification</h4>
                    <p className="text-sm text-gray-500">Corriger vos données inexactes</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-100">
                    <h4 className="font-semibold text-gray-900 mb-1">Droit à l'effacement</h4>
                    <p className="text-sm text-gray-500">Demander la suppression de vos données</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-100">
                    <h4 className="font-semibold text-gray-900 mb-1">Droit à la portabilité</h4>
                    <p className="text-sm text-gray-500">Recevoir vos données dans un format lisible</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Contact</h2>
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                <p className="text-gray-600 mb-4">
                  Pour toute question concernant cette politique ou pour exercer vos droits, contactez-nous :
                </p>
                <div className="space-y-2">
                  <p className="text-gray-600"><strong>Email :</strong> privacy@alphaswapp.com</p>
                  <p className="text-gray-600"><strong>Adresse :</strong> Paris, France</p>
                </div>
              </div>
            </section>
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-8 border-t border-gray-100">
            <Link to="/" className="flex items-center space-x-2 text-gray-500 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Retour à l'accueil</span>
            </Link>
            <div className="flex space-x-4">
              <Link to="/cookies" className="text-[#191C1F] hover:underline">Cookies</Link>
              <Link to="/agreement" className="text-[#191C1F] hover:underline">Conditions</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
